import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  History,
  GitBranch,
  Calendar,
  Tag,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Bug,
  Wrench,
  Shield,
  AlertCircle
} from 'lucide-react';

// Changelog data - this would typically come from an API
const changelogData = [
  {
    version: '2.0.0',
    date: '2026-03-14',
    title: 'Major Release - 3D Floor Plan System & SOW Integration',
    status: 'current',
    changes: [
      { type: 'feature', description: '3D Floor Plan Visualization System with AI-powered renders' },
      { type: 'feature', description: 'SOW (Scope of Work) Integration - auto-populate render settings' },
      { type: 'feature', description: '50+ finish options for 3D renders (flooring, cabinets, countertops, etc.)' },
      { type: 'feature', description: '7 toggleable 2D layers (Electrical, Plumbing, Lighting, HVAC, Flooring, Walls, Furniture)' },
      { type: 'feature', description: 'Element placement on floor plans with real-time updates' },
      { type: 'feature', description: 'Finish Library for saving preset combinations' },
      { type: 'feature', description: 'Advanced Reporting dashboard with analytics' },
      { type: 'improvement', description: 'Portal login button added to main website navbar' },
      { type: 'improvement', description: 'Home link added to navigation' },
      { type: 'improvement', description: 'Scroll to top on page navigation' },
      { type: 'improvement', description: 'Settings page with profile and password management' },
      { type: 'fix', description: 'Backend test suite updated for floor plan fixtures' }
    ]
  },
  {
    version: '1.5.0',
    date: '2026-03-14',
    title: 'Customer Portal & Calendar Integration',
    status: 'archived',
    changes: [
      { type: 'feature', description: 'Customer Portal with separate authentication' },
      { type: 'feature', description: 'Customer project view with progress tracking' },
      { type: 'feature', description: 'Equipment Maintenance Scheduling module' },
      { type: 'feature', description: 'Calendar Integration with event management' },
      { type: 'feature', description: 'Google Calendar sync (ICS export)' },
      { type: 'improvement', description: 'Mobile portal responsiveness improvements' }
    ]
  },
  {
    version: '1.4.0',
    date: '2026-03-13',
    title: 'Document Templates & Client Share Links',
    status: 'archived',
    changes: [
      { type: 'feature', description: 'Document Template Library (Contract, Change Order, SOW, etc.)' },
      { type: 'feature', description: 'Client Share Page with secure token-based access' },
      { type: 'feature', description: 'AI Document Generation capability' },
      { type: 'improvement', description: 'Document categorization and filtering' }
    ]
  },
  {
    version: '1.3.0',
    date: '2026-03-12',
    title: 'Accounting & Business Planning (COO Only)',
    status: 'archived',
    changes: [
      { type: 'feature', description: 'Full Accounting Suite with invoices, expenses, P&L' },
      { type: 'feature', description: 'Business Planning module with goals and forecasting' },
      { type: 'feature', description: 'Legal/Proprietary Notice page' },
      { type: 'security', description: 'Role-based access control for sensitive modules' }
    ]
  },
  {
    version: '1.2.0',
    date: '2026-03-11',
    title: 'Team & Inventory Management',
    status: 'archived',
    changes: [
      { type: 'feature', description: 'Team Builder for crew management' },
      { type: 'feature', description: 'Inventory Management with equipment tracking' },
      { type: 'feature', description: 'Equipment checkout system' },
      { type: 'improvement', description: 'Dashboard statistics and charts' }
    ]
  },
  {
    version: '1.1.0',
    date: '2026-03-10',
    title: 'Jobs Pipeline & Documents',
    status: 'archived',
    changes: [
      { type: 'feature', description: 'Jobs Pipeline with full CRUD operations' },
      { type: 'feature', description: 'Job detail page with phases and tasks' },
      { type: 'feature', description: 'Documents module for file management' },
      { type: 'improvement', description: 'Improved navigation and UI polish' }
    ]
  },
  {
    version: '1.0.0',
    date: '2026-03-09',
    title: 'Initial Release - Portal Foundation',
    status: 'archived',
    changes: [
      { type: 'feature', description: 'Owner Portal with dual-role authentication (COO/President)' },
      { type: 'feature', description: 'Dashboard with overview metrics' },
      { type: 'feature', description: 'Public website with services, projects, and contact' },
      { type: 'security', description: 'JWT-based authentication system' },
      { type: 'security', description: 'Role-based permissions (COO vs President)' }
    ]
  }
];

const getChangeIcon = (type) => {
  switch (type) {
    case 'feature': return Sparkles;
    case 'improvement': return Wrench;
    case 'fix': return Bug;
    case 'security': return Shield;
    default: return AlertCircle;
  }
};

const getChangeColor = (type) => {
  switch (type) {
    case 'feature': return 'text-green-600 bg-green-50';
    case 'improvement': return 'text-blue-600 bg-blue-50';
    case 'fix': return 'text-orange-600 bg-orange-50';
    case 'security': return 'text-purple-600 bg-purple-50';
    default: return 'text-gray-600 bg-gray-50';
  }
};

export const Updates = () => {
  const { user } = useAuth();
  const [expandedVersions, setExpandedVersions] = useState(['2.0.0']);
  const [filter, setFilter] = useState('all');

  const toggleVersion = (version) => {
    setExpandedVersions(prev =>
      prev.includes(version)
        ? prev.filter(v => v !== version)
        : [...prev, version]
    );
  };

  const filteredChangelog = filter === 'all'
    ? changelogData
    : changelogData.filter(release =>
        release.changes.some(change => change.type === filter)
      );

  return (
    <div className="space-y-6" data-testid="updates-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl flex items-center gap-3">
            <History className="w-8 h-8 text-[#C6A87C]" />
            Updates & Changelog
          </h1>
          <p className="text-gray-500 mt-1">
            Track all updates, improvements, and fixes to the portal
          </p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex flex-wrap gap-2">
        {['all', 'feature', 'improvement', 'fix', 'security'].map((filterType) => (
          <button
            key={filterType}
            onClick={() => setFilter(filterType)}
            className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${
              filter === filterType
                ? 'bg-[#C6A87C] text-white'
                : 'bg-white border border-gray-200 text-gray-600 hover:border-[#C6A87C]'
            }`}
          >
            {filterType === 'all' ? 'All Updates' : filterType + 's'}
          </button>
        ))}
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />

        {/* Releases */}
        <div className="space-y-6">
          {filteredChangelog.map((release, idx) => (
            <motion.div
              key={release.version}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="relative pl-16"
            >
              {/* Timeline dot */}
              <div className={`absolute left-4 w-5 h-5 rounded-full border-4 border-white ${
                release.status === 'current' ? 'bg-[#C6A87C]' : 'bg-gray-300'
              }`} />

              {/* Release card */}
              <div className={`bg-white border ${
                release.status === 'current' ? 'border-[#C6A87C]' : 'border-gray-100'
              }`}>
                {/* Header */}
                <button
                  onClick={() => toggleVersion(release.version)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-gray-400" />
                      <span className={`font-mono font-bold ${
                        release.status === 'current' ? 'text-[#C6A87C]' : 'text-gray-600'
                      }`}>
                        v{release.version}
                      </span>
                      {release.status === 'current' && (
                        <span className="px-2 py-0.5 bg-[#C6A87C] text-white text-xs font-semibold">
                          CURRENT
                        </span>
                      )}
                    </div>
                    <span className="text-gray-400">|</span>
                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                      <Calendar className="w-4 h-4" />
                      {new Date(release.date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-500">
                      {release.changes.length} changes
                    </span>
                    {expandedVersions.includes(release.version) ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Title */}
                <div className="px-4 pb-4 border-b border-gray-100">
                  <h3 className="font-heading text-lg">{release.title}</h3>
                </div>

                {/* Changes */}
                {expandedVersions.includes(release.version) && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    className="p-4 space-y-2"
                  >
                    {release.changes
                      .filter(change => filter === 'all' || change.type === filter)
                      .map((change, changeIdx) => {
                        const Icon = getChangeIcon(change.type);
                        const colorClass = getChangeColor(change.type);
                        return (
                          <div
                            key={changeIdx}
                            className="flex items-start gap-3 p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                          >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <div>
                              <span className={`text-xs font-semibold uppercase ${colorClass.split(' ')[0]}`}>
                                {change.type}
                              </span>
                              <p className="text-gray-700 mt-1">{change.description}</p>
                            </div>
                          </div>
                        );
                      })}
                  </motion.div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-gray-50 p-4 border border-gray-100">
        <h4 className="font-semibold mb-3">Change Types</h4>
        <div className="flex flex-wrap gap-4">
          {[
            { type: 'feature', label: 'New Feature' },
            { type: 'improvement', label: 'Improvement' },
            { type: 'fix', label: 'Bug Fix' },
            { type: 'security', label: 'Security Update' }
          ].map(item => {
            const Icon = getChangeIcon(item.type);
            const colorClass = getChangeColor(item.type);
            return (
              <div key={item.type} className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${colorClass}`}>
                  <Icon className="w-3 h-3" />
                </div>
                <span className="text-sm text-gray-600">{item.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
